import {
  Box,
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  CrudMode,
  ListContext,
  ListContextValue,
  Page,
  PageRoute,
  TabContext,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { Application, ApplicationEditResource, ApplicationViewResource, Permission } from "generated/openApi";
import produce from "immer";
import "moment-timezone";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { ApplicationPageHeader } from "./ApplicationPageHeader";
import { ApplicationPageTabs } from "./ApplicationPageTabs";
import { ApplicationGeneralInfo } from "./General/ApplicationGeneralInfo";
import { ApplicationQuestions } from "./Questions/ApplicationQuestions";
import { ApplicationSections } from "./Sections/ApplicationSections";

const routeName = "application";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
}

class OptionalParams {
  tab?: ApplicationTab | undefined = undefined;
}

export const applicationPageRoute: PageRoute<Params, OptionalParams> = {
  handle: "Application",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
  getOptionalParams: (params) => buildOptionalParams(params),
};

export type ApplicationTab = "General" | "Questions" | "Sections";

const ROOT_PERMISSION: Permission = Permission.Contributor;

export const ApplicationPage = () => {
  const navigate = useNavigate();
  const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
  const [currentTab, setCurrentTab] = useState<ApplicationTab>(optionalParams.tab || "General");
  const [application, setApplication] = useState<Application>({} as Application);
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [application] });
  const [refreshApplication, setRefreshApplication] = useState<Application>();
  const [copyApplication, setCopyApplication] = useState<number>();
  const [viewResource, setViewResource] = useState<ApplicationViewResource>({} as ApplicationViewResource);
  const [editResource, setEditResource] = useState<ApplicationEditResource>({});

  const { validationErrors, setValidationErrors } = useValidationErrors();

  // keeps track of which tabs data are loaded
  const [visitedTabs, setVisitedTabs] = useState<ApplicationTab[]>([]);

  useEffect(() => {
    fetchApplication(params.id);
    fetchViewResource(params.id);
  }, []);

  useEffect(() => {
    if (params.crudMode === "edit") fetchEditResource(params.id);
  }, [params.crudMode]);

  // load data from each tab as it's selected
  useEffect(() => {
    if (!visitedTabs.includes(currentTab)) {
      setVisitedTabs(
        produce((draft) => {
          draft.push(currentTab);
        })
      );
    }
  }, [currentTab]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(`../${applicationPageRoute.getPath({ id: id, crudMode: crudMode })}`);

  const fetchApplication = (id: number) => {
    ApiService.applications.get(id).then((response) => {
      setApplication(response);
      setRefreshApplication(response);
      resetUnsavedChanges([response]);
    });
  };

  const fetchViewResource = (id: number) => {
    ApiService.applications.getViewResource(id).then((response) => {
      setViewResource(response);
    });
  };

  const fetchEditResource = (id: number) => {
    ApiService.applications.getEditResource(id).then((response) => {
      setEditResource(response);
    });
  };

  return (
    <TabContext value={currentTab}>
      <Page
        isForm
        header={
          <ApplicationPageHeader
            rootPermission={ROOT_PERMISSION}
            application={application}
            crudMode={params.crudMode}
            fetch={fetchApplication}
            setValidationErrors={setValidationErrors}
            unsavedChanges={unsavedChanges}
            reroute={reroute}
          />
        }
        tabs={<ApplicationPageTabs viewResource={viewResource} setCurrentTab={setCurrentTab} />}
      >
        <Box hidden={currentTab !== "General"}>
          <ApplicationGeneralInfo
            application={application}
            setApplication={setApplication}
            setCopyApplication={setCopyApplication}
            viewResource={viewResource}
            editResource={editResource}
            crudMode={params.crudMode}
            validationErrors={validationErrors}
          />
        </Box>

        <Box hidden={currentTab !== "Sections"}>
          {application && (
            <ApplicationSections
              application={application}
              navigateEditMode={() => reroute(application.id, "edit")}
              copyApplication={copyApplication}
              refreshApplication={refreshApplication}
              setApplication={setApplication}
              viewResource={viewResource}
              editResource={editResource}
              crudMode={params.crudMode}
              validationErrors={validationErrors}
            />
          )}
        </Box>

        <Box hidden={currentTab !== "Questions"}>
          {application && (
            <ApplicationQuestions
              application={application}
              copyApplication={copyApplication}
              setApplication={setApplication}
              refreshApplication={refreshApplication}
              viewResource={viewResource}
              editResource={editResource}
              crudMode={params.crudMode}
              validationErrors={validationErrors}
              navigateEditMode={() => reroute(application.id, "edit")}
            />
          )}
        </Box>
      </Page>
    </TabContext>
  );
};
